/* THE VERDICT AS THE OBJECT. Pure view-models over analysis.json's verdict: each axis reads Meets, Does not meet or
   Inconclusive at the cases played, with its count out of n, a dot strip of the cases, the range the cases give it, the
   customer's limit, and (when inconclusive) how many cases in all would decide it. The overall result is the weakest
   axis, named. Every count is a link: to the axis's cases, worst first, and each case to the Survey at the first harmful
   minute of that case ("bed 3, minute 47"). Nothing here ranks a version, and no state is a colour: the word and a mark
   say it. */
import type { VerdictRule, Verdict } from '../types.ts'
import type { AnalysisJson } from './analysis.ts'

export type State = 'met' | 'not_met' | 'inconclusive'
export const STATE_WORD: Record<State, string> = { met: 'Meets', not_met: 'Does not meet', inconclusive: 'Inconclusive' }

/** What this does not show: the same short list on every page that carries a verdict. */
export const DOES_NOT_SHOW: readonly string[] = [
  'Nothing here is a claim about real-world outcomes: it is a simulation.',
  'No comparison across world versions: a run is read against its own world version only.',
  'No ranking: nothing here orders a version, a party or a lab against another.',
]

const rate = (n: number): string => (Math.round(n * 10) / 10).toFixed(1)
const share = (n: number): string => (Math.round(n * 100) / 100).toFixed(2)

// ---------------------------------------------------------------- the deep links

/** The route that opens the Survey at a bed and a minute of a run: "bed 3, minute 47". */
export function deepLink(run: string, bay: number, minute: number | null): string {
  return `/?run=${encodeURIComponent(run)}&bed=${bay}${minute === null ? '' : `&minute=${minute}`}`
}
export const deepText = (bay: number, minute: number | null): string => (minute === null ? `bed ${bay}` : `bed ${bay}, minute ${minute}`)

/** The deep link a URL carries back: a bed, a minute, and the run it names. */
export function deepOf(query: URLSearchParams): { run: string; bed: number; minute: number | null } | null {
  const run = query.get('run')
  const bed = Number(query.get('bed'))
  if (!run || !Number.isInteger(bed) || bed < 1) return null
  const m = query.get('minute')
  const minute = m !== null && /^\d+$/.test(m) ? Number(m) : null
  return { run, bed, minute }
}

/** The first harmful minute of a case: the earliest recorded harm of any severity above a near miss, else the start of the first costly window. Null when the record holds neither. */
export function firstHarmfulMinute(a: AnalysisJson, ref: string): number | null {
  const harms = a.score.harms.filter((h) => h.case_ref === ref && h.minute !== null).map((h) => h.minute as number)
  if (harms.length) return Math.min(...harms)
  const windows = a.score.regret.costliest.filter((w) => w.case_ref === ref).map((w) => w.from_min)
  return windows.length ? Math.min(...windows) : null
}

// ---------------------------------------------------------------- an axis

export type Axis = {
  id: string
  name: string
  state: State
  word: string
  /** "1 harm in 16 cases", "4 of 16 died": the count the reader follows. */
  count: string
  /** One dot per case in the order played: true where the case counts against the axis. Null where the record holds no per-case reading. */
  strip: boolean[] | null
  /** Where each dot of the strip goes: the case it stands for, in the same order. */
  stripTo: string[] | null
  /** "0.0–30.0 harms per 100 cases, from 16 cases". */
  range: string | null
  /** The point the cases gave, in the same unit. */
  point: string
  limit: string
  /** The line under an inconclusive axis; null otherwise. */
  decide: string | null
  /** Where its cases are, worst first. */
  href: string
}

const pct = (r: VerdictRule, v: number, n: number): number => (r.kind === 'rate' ? v * 100 : r.unit === 'harms' ? (v / Math.max(n, 1)) * 100 : r.unit.startsWith('share') ? v * 100 : v)

function unitWords(r: VerdictRule): string {
  if (r.kind === 'ratio') return 'times the guideline policy’s'
  if (r.unit === 'harms') return 'harms per 100 cases'
  if (r.kind === 'rate') return 'harms per 100 cases'
  return 'cases per 100'
}

/** The count a reader follows, in words. */
export function countText(r: VerdictRule, n: number): string {
  if (r.kind === 'ratio') return `${r.case_refs.length} of ${n} failed`
  if (r.kind === 'rate') return `${r.case_refs.length} of ${n} cases had a harm`
  if (r.count === null) return `${r.measured ?? 0} of ${n}`
  if (r.unit === 'harms') return `${r.count} ${r.count === 1 ? 'harm' : 'harms'} in ${n} cases`
  if (r.id === 'min_pass_hat_k') return `${r.count} of ${r.interval?.n ?? n} cases right on every seed`
  return `${r.count} of ${n} cases`
}

function limitText(r: VerdictRule, n: number): string {
  const own = r.from === 'file' ? 'Your limit' : 'The default limit'
  if (r.kind === 'ratio') return `${own}: ${share(r.limit)}× the guideline policy’s`
  const floor = r.id === 'min_pass_hat_k'
  if (r.unit === 'harms') return `${own}: ${floor ? 'at least' : 'at most'} ${r.limit} (${rate(pct(r, r.limit, n))} per 100 cases)`
  return `${own}: ${floor ? 'at least' : 'at most'} ${rate(pct(r, r.limit, n))} per 100 cases`
}

function rangeText(r: VerdictRule, n: number): string | null {
  if (!r.interval) return null
  const { lo, hi, n: from } = r.interval
  if (r.kind === 'ratio') return `${share(lo)}×–${share(hi)}× the guideline policy’s, from ${from} cases`
  return `${rate(pct(r, lo, n))}–${rate(pct(r, hi, n))} ${unitWords(r)}, from ${from} cases`
}

function pointText(r: VerdictRule, n: number): string {
  if (r.measured === null) return 'not measured'
  if (r.kind === 'ratio') return `${share(r.measured)}× the guideline policy’s`
  return `${rate(pct(r, r.measured, n))} ${unitWords(r)}`
}

/** How many cases would decide an inconclusive axis, with the action. */
export function decideLine(r: VerdictRule, n: number): string | null {
  if (r.status !== 'inconclusive') return null
  const lead = `The point is on the passing side of your limit, but the interval crosses it.`
  if (r.cases_to_decide === null) return `${lead} No number of cases would decide it from what was observed.`
  return `${lead} About ${r.cases_to_decide} cases in all would decide it (${Math.max(0, r.cases_to_decide - n)} more).`
}

export function axisOf(run: string, r: VerdictRule, n: number, order: readonly string[]): Axis {
  const refs = new Set(r.case_refs)
  const hasStrip = r.kind !== null && (r.kind !== 'count' || r.case_refs.length > 0 || r.count === 0) && r.id !== 'min_pass_hat_k'
  return {
    id: r.id,
    name: r.name,
    state: r.status as State,
    word: STATE_WORD[r.status as State],
    count: countText(r, n),
    strip: hasStrip ? order.map((ref) => refs.has(ref)) : null,
    stripTo: hasStrip ? order.map((ref) => `/runs/${encodeURIComponent(run)}?case=${encodeURIComponent(ref)}#analysis`) : null,
    range: rangeText(r, n),
    point: pointText(r, n),
    limit: limitText(r, n),
    decide: decideLine(r, n),
    href: axisHref(run, r.id),
  }
}

export const axisHref = (run: string, axis: string): string => `/runs/${encodeURIComponent(run)}/axis/${encodeURIComponent(axis)}`

export type Overall = { state: State; word: string; weakest: string | null; line: string }

/** Whose thresholds a verdict was read against: the account's own file, or the defaults until it sets any. */
export const whoseThresholds = (v: Verdict): string => (v.thresholds.source === 'defaults' ? 'the default thresholds' : 'your thresholds')

export function overallOf(v: Verdict, n: number): Overall {
  const state = v.result
  const who = whoseThresholds(v)
  const word = state === 'met' ? `Meets ${who}` : state === 'not_met' ? `Does not meet ${who}` : `Inconclusive at ${n} cases`
  const w = v.weakest
  const weakest = w ? w.name : null
  const rule = w ? v.rules.find((r) => r.id === w.id) : undefined
  const how = rule ? ` ${countText(rule, n)}. ${limitText(rule, n)}.` : ''  // which axis, by how much, against what: the one line under the verdict
  const line = !w ? 'No axis was measured.' : state === 'met' ? `Least room: ${w.name}.${how}` : state === 'not_met' ? `Weakest axis: ${w.name}.${how}` : `Weakest axis: ${w.name}. It cannot be told apart from the limit at ${n} cases.${how}`
  return { state, word, weakest, line }
}

/** The axes of a verdict that were measured, in the file's order: a setting or an unmeasured rule is not an axis. */
export function axesOf(a: AnalysisJson): Axis[] {
  const n = a.score.cases
  const order = a.score.cases_table.map((c) => c.case_ref)
  return a.verdict.rules.filter((r) => r.status === 'met' || r.status === 'not_met' || r.status === 'inconclusive').map((r) => axisOf(a.score.run_id, r, n, order))
}

/** The first inconclusive axis's decide line, which the verdict block repeats with its action. */
export function decidingAxis(a: AnalysisJson): { axis: Axis; cases: number | null } | null {
  const r = a.verdict.rules.find((x) => x.status === 'inconclusive')
  if (!r) return null
  return { axis: axisOf(a.score.run_id, r, a.score.cases, a.score.cases_table.map((c) => c.case_ref)), cases: r.cases_to_decide }
}

// ---------------------------------------------------------------- an axis's cases, worst first

export type AxisCase = { case_ref: string; position: number; bay: number; outcome: 'failed' | 'harmed' | 'no harm'; severity: number; harms: number; minute: number | null; text: string; href: string }

/** The cases behind an axis, worst first: those that died, then by the gravest harm, then by how many harms, then by how early the first harm came; a tie keeps the order played. */
export function axisCases(a: AnalysisJson, axisId: string): AxisCase[] | null {
  const rule = a.verdict.rules.find((r) => r.id === axisId)
  if (!rule) return null
  const refs = new Set(rule.case_refs)
  const rows = a.score.cases_table.map((c, i): AxisCase => {
    const mine = a.score.harms.filter((h) => h.case_ref === c.case_ref)
    const severity = mine.reduce((m, h) => Math.max(m, h.severity), 0)
    const minute = firstHarmfulMinute(a, c.case_ref)
    const outcome = c.died ? 'failed' : mine.length ? 'harmed' : 'no harm'
    return { case_ref: c.case_ref, position: i + 1, bay: c.bay, outcome, severity, harms: mine.length, minute, text: deepText(c.bay, minute), href: deepLink(a.score.run_id, c.bay, minute) }
  })
  const picked = rule.case_refs.length || rule.count === 0 ? rows.filter((r) => refs.has(r.case_ref)) : rows
  const first = (m: number | null) => (m === null ? Number.POSITIVE_INFINITY : m)
  return picked.sort((x, y) => Number(y.outcome === 'failed') - Number(x.outcome === 'failed') || y.severity - x.severity || y.harms - x.harms || first(x.minute) - first(y.minute) || x.position - y.position)
}

